/**
 * 爱微圈导航 · 时钟 / 问候 / 天气
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { $, $$, API, API_TIMEOUT_MS, CACHE_KEY_LEGACY, DEFAULT_SETTINGS, apiGet, apiPost, bootData, cacheKey, debounce, esc, escAttr, fetchWithTimeout, randomColor, safeUrl, shouldAnimate, state, toast } from './core.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { showContextMenu, showEditModal, showFolderModal } from './edit.js';
import { BUILTIN_ACTIONS, init } from './main.js';
import { closeModal, createCloudBackup, loadBackupListInto, modalConfirm, openModal, runDataRepair, setupDataButtons, showBackupList, showModalError, showQrSync } from './modal.js';
import { ONBOARDING_STEPS, closeOnboarding, maybeShowOnboarding, nextOnbStep, renderOnbStep, showOnboarding } from './onboarding.js';
import { batchDelete, batchMoveToPage, enforcePageCapacity, getPageCapacity, goNextPage, goPrevPage, initBatchToolbar, loadShortcuts, loadShortcutsRaw, prefetchAdjacentPages, prefetchPage, renderPages, shiftPagesAfterRemoval, switchPage, toggleOrganizeMode, updateBatchToolbar, updatePageArrows } from './pages.js';
import { _readIconStore, _reduceMotion, _saveIconStore, _scFp, cardRenderFp, currentGrid, faviconOf, flipMove, getGridCols, gridRenderFp, persistGridReorder, preconnectUrl, renderCardView, renderCardViewAll, renderIcon, renderShortcuts, renderSkeleton, waitTransition } from './render.js';
import { TAB_TARGETS, bindEngineIconFallback, currentEngine, engineBadgeHtml, engineFamily, fetchSuggestions, handleSearch, hideSuggestions, openTarget, renderEngineBadge, renderEngineDropdown, showEngineAddModal } from './search.js';
import { applySettings, bindSettingsUI, loadSettings, scheduleSave } from './settings.js';
import { bindDonateCard, bindShareCard, detectPayApp, fmtDonateTime, loadDonateRank, renderDonateRank, shareQrSrc, shareTargetUrl, shareTextDesc, shareTextTitle, showAboutModal, showContactModal, showDonateModal, showFeedbackModal, showPrivacyModal, showShareModal, showTermsModal } from './share.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { CACHE_TTL, CATEGORY_ENGINE_MAP, applySearchTabUI, cacheState, disableNotifications, enableNotifications, mapCategoryToEngine, notifyEnabled, restoreCache, setNotifyEnabled, showNotification, submitSiteForReview, syncEnabled, toggleSync, trackVisit } from './sync.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

export function greeting(h) {
    const s = state.site || {};
    if (h < 5) return s.greeting_night || '夜深了，注意休息';
    if (h < 9) return s.greeting_morning || '早上好';
    if (h < 12) return s.greeting_forenoon || '上午好';
    if (h < 14) return s.greeting_noon || '中午好';
    if (h < 18) return s.greeting_afternoon || '下午好';
    if (h < 22) return s.greeting_evening || '晚上好';
    return s.greeting_night || '夜深了，注意休息';
}
/* 渲染时钟下方文本：主文本 + 可选作者小字（安全：textContent 防 XSS） */

export function renderClockGreet(text, author) {
    const el = $('#clockGreet');
    if (!el) return;
    el.textContent = text || '';
    if (author) {
        const span = document.createElement('span');
        span.className = 'daily-author';
        span.style.cssText = 'font-size:0.72em;opacity:0.65;margin-left:6px;white-space:nowrap;';
        span.textContent = '— ' + author;
        el.appendChild(span);
    }
}
/* 拉取每日内容（名言/励志/诗词/轮播），当天缓存到 state.daily */

export async function loadDailyContent(mode) {
    if (!mode || mode === 'greeting') return;
    const today = new Date().toISOString().slice(0, 10);
    if (state.daily && state.daily[mode] && state.daily[mode].date === today) return;
    try {
        const data = await apiGet('/api/daily.php?action=today&type=' + encodeURIComponent(mode));
        if (!state.daily) state.daily = {};
        state.daily[mode] = data;
        // 当前显示模式就是这个，立即更新
        if ((state.settings.daily_greet_mode || 'greeting') === mode) tickClock();
    } catch (e) { /* 静默失败，保持问候语 */ }
}

export function tickClock() {
    const now = new Date();
    const h = String(now.getHours()).padStart(2, '0');
    const m = String(now.getMinutes()).padStart(2, '0');
    if (state.settings.show_seconds === '1') {
        const s = String(now.getSeconds()).padStart(2, '0');
        $('#clockTime').innerHTML = `${h}:${m}<span class="clock-sec">:${s}</span>`;
    } else {
        $('#clockTime').textContent = `${h}:${m}`;
    }
    // 时钟下方：时段问候语 / 每日名言 / 每日励志 / 每日诗词 / 每日轮播
    const dm = state.settings.daily_greet_mode || 'greeting';
    if (dm === 'greeting') {
        renderClockGreet(greeting(now.getHours()), '');
    } else {
        const daily = state.daily && state.daily[dm];
        if (daily && daily.content) {
            renderClockGreet(daily.content, daily.author);
        } else {
            // 内容尚未加载，先用问候语占位，loadDailyContent 完成后会自动 tickClock 更新
            renderClockGreet(greeting(now.getHours()), '');
        }
    }
    const wd = ['日', '一', '二', '三', '四', '五', '六'][now.getDay()];
    $('#clockDate').textContent = `${now.getMonth() + 1} 月 ${now.getDate()} 日 星期${wd}`;
}

/* ---------- 天气 ---------- */
/* 浏览器自动定位：成功后按坐标查天气并保存城市；失败/拒绝静默回退 weather_city */

export function weatherGeolocate() {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error('浏览器不支持定位'));
        navigator.geolocation.getCurrentPosition(
            pos => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
            err => reject(new Error(err.message || '定位失败')),
            { timeout: 8000, maximumAge: 600000 }
        );
    });
}

export async function applyWeather(w) {
    state.weather = w;
    state._weatherLoadedAt = Date.now();
    $('#weatherIcon').textContent = w.icon;
    $('#weatherTemp').textContent = w.temp + '°';
    $('#weatherCity').textContent = w.city;
    renderWeatherPop();
}

export async function loadWeather() {
    if (state.settings.show_weather === '0' || state.site.weather_enabled === '0') return;
    try {
        /* 首次访问：询问一次自动定位（localStorage 记住决定，不再反复打扰） */
        if (!localStorage.getItem('weatherGeoAsked')) {
            localStorage.setItem('weatherGeoAsked', '1');
            try {
                const c = await weatherGeolocate();
                const w = await apiGet(API.weather + '?lat=' + encodeURIComponent(c.lat) + '&lon=' + encodeURIComponent(c.lon));
                if (w && w.city) { state.settings.weather_city = w.city; scheduleSave(); }
                await applyWeather(w);
                toast('已自动定位到 ' + (w.city || '当前位置'), 'success');
                return;
            } catch (e) { /* 定位失败：回退默认城市 */ }
        }
        const w = await apiGet(API.weather);
        await applyWeather(w);
    } catch (e) {
        /* 失败保留上次数据（state.weather 不清空，天气 chip 仍显示旧值）；
         * 从未成功过时 10s 后自动重试一次，避免一次网络抖动就整段会话显示 "--" */
        if (!state.weather && !state._weatherRetried) {
            state._weatherRetried = true;
            setTimeout(() => { if (!state.weather) loadWeather(); }, 10000);
        }
    }
}

export function renderWeatherPop() {
    const w = state.weather;
    if (!w) return;
    const pop = $('#weatherPop');
    const days = (w.forecast || []).map((d, i) => {
        const label = i === 0 ? '今天' : i === 1 ? '明天' : '后天';
        return `<div class="wp-day"><div class="d-label">${label}</div><div class="d-icon">${d.icon}</div><div class="d-range">${d.min}° / ${d.max}°</div></div>`;
    }).join('');
    pop.innerHTML = `
        <div class="wp-now">
            <div class="w-icon">${w.icon}</div>
            <div><div class="w-temp">${w.temp}°C</div><div class="w-text">${esc(w.city)} · ${esc(w.text)}</div></div>
        </div>
        <div class="wp-meta"><span>湿度 ${w.humidity}%</span><span>风速 ${w.wind} km/h</span><span>体感 ${w.feels}°</span></div>
        ${w.stale ? '<div class="wp-stale">网络异常，显示的是稍早缓存的数据</div>' : ''}
        <div class="wp-days">${days}</div>
        <div class="wp-city-input">
            <span class="wp-city-ico">📍</span>
            <input type="text" id="weatherCityInput" placeholder="切换城市，如：上海" maxlength="20" value="${escAttr(w.city)}">
            <button type="button" class="wp-geo-btn" id="weatherGeoApply" title="使用浏览器定位获取当前位置天气">定位</button>
            <button type="button" class="wp-go-btn" id="weatherCityApply">查询</button>
        </div>`;
    $('#weatherCityApply').onclick = async () => {
        const city = $('#weatherCityInput').value.trim();
        if (!city) return;
        try {
            const w2 = await apiGet(API.weather + '?city=' + encodeURIComponent(city));
            state.settings.weather_city = city;
            scheduleSave();
            await applyWeather(w2);
            toast(`已切换到 ${w2.city}`, 'success');
        } catch (e) { toast(e.message || '未找到该城市', 'error'); }
    };
    $('#weatherGeoApply').onclick = async () => {
        const btn = $('#weatherGeoApply');
        btn.disabled = true; btn.textContent = '定位中…';
        try {
            const c = await weatherGeolocate();
            const w2 = await apiGet(API.weather + '?lat=' + encodeURIComponent(c.lat) + '&lon=' + encodeURIComponent(c.lon));
            if (w2 && w2.city) { state.settings.weather_city = w2.city; scheduleSave(); }
            await applyWeather(w2);
            toast(`已定位到 ${w2.city || '当前位置'}`, 'success');
        } catch (e) {
            toast('定位失败，请检查浏览器定位权限', 'error');
            const b2 = $('#weatherGeoApply');
            if (b2) { b2.disabled = false; b2.textContent = '定位'; }
            return;
        }
        const b3 = $('#weatherGeoApply');
        if (b3) { b3.disabled = false; b3.textContent = '定位'; }
    };
    $('#weatherCityInput').onkeydown = (e) => { if (e.key === 'Enter') $('#weatherCityApply').click(); };
}

export function toggleWeatherPop() { $('#weatherPop').classList.toggle('show'); }

/* ---------- 搜索 ---------- */

